import React, { useEffect, useState } from "react";
import { FaNetworkWired } from "react-icons/fa";
import TaskModalRoot from "../TaskManagement/TaskModalRoot";
import { CMS_PRIMARY } from "../TaskManagement/workspaceConstants";
import { INPUT_CLASS, LABEL_CLASS } from "./accountFormShared";

const MODES = [
  { value: "set_ips", label: "Replace IPs", description: "Overwrite existing IPs for all selected users" },
  { value: "append_ips", label: "Add IPs", description: "Append to each user's existing IP list" },
  { value: "enable", label: "Enable only", description: "Turn on IP restriction without changing stored IPs" },
  { value: "clear_ips", label: "Clear IPs", description: "Remove stored IPs but keep restriction setting" },
];

function BulkIpModal({ open, selectedCount = 0, loading = false, onClose, onSubmit }) {
  const [mode, setMode] = useState("set_ips");
  const [ipAddress, setIpAddress] = useState("");

  useEffect(() => {
    if (!open) return;
    setMode("set_ips");
    setIpAddress("");
  }, [open]);

  const needsIpInput = mode === "set_ips" || mode === "append_ips";

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit?.({
      action: mode,
      ip_address: needsIpInput ? ipAddress : "",
    });
  };

  if (!open) return null;

  return (
    <TaskModalRoot open={open}>
      <div className="relative z-10 w-full max-w-lg rounded-2xl border border-[#E5E7EB] bg-white shadow-2xl">
        <div className="border-b border-gray-100 px-5 py-4">
          <div
            className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl"
            style={{ backgroundColor: CMS_PRIMARY }}
          />
          <div className="flex items-start gap-3">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-sm"
              style={{ backgroundColor: CMS_PRIMARY }}
            >
              <FaNetworkWired size={16} />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-[#35314d]">Bulk IP Management</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                Apply IP settings to {selectedCount} selected account
                {selectedCount === 1 ? "" : "s"}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-4">
          <div className="space-y-3">
            <div>
              <p className={LABEL_CLASS}>Action</p>
              <div className="space-y-2">
                {MODES.map((item) => (
                  <label
                    key={item.value}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition ${
                      mode === item.value
                        ? "border-[#007BFF]/30 bg-[#007BFF]/5"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="bulk-ip-mode"
                      value={item.value}
                      checked={mode === item.value}
                      onChange={() => setMode(item.value)}
                      className="mt-0.5"
                    />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-gray-800">
                        {item.label}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {item.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {needsIpInput ? (
              <div>
                <label htmlFor="bulk-ip-address" className={LABEL_CLASS}>
                  IP Address(es) <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="bulk-ip-address"
                  className={`${INPUT_CLASS} min-h-[88px] py-2.5`}
                  value={ipAddress}
                  onChange={(e) => setIpAddress(e.target.value)}
                  placeholder="e.g. 203.0.113.10, 203.0.113.11"
                  disabled={loading}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Separate multiple IPs with commas, spaces, or new lines
                </p>
              </div>
            ) : null}
          </div>

          <div className="mt-5 flex items-center justify-end gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex h-10 items-center rounded-lg border border-[#E5E7EB] bg-white px-4 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || (needsIpInput && !ipAddress.trim())}
              className="inline-flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold text-white shadow-md transition hover:brightness-95 disabled:opacity-70"
              style={{
                backgroundColor: CMS_PRIMARY,
                boxShadow: "0 2px 8px rgba(0, 123, 255, 0.28)",
              }}
            >
              {loading ? "Applying..." : "Apply to selected"}
            </button>
          </div>
        </form>
      </div>
    </TaskModalRoot>
  );
}

export default BulkIpModal;
